import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Card, { CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import {
  FileTextIcon,
  TrendingUpIcon,
  WalletIcon,
  MessageCircleIcon,
  ShieldCheckIcon,
  CalendarIcon,
  DownloadIcon,
  ChevronDownIcon,
  CheckIcon,
} from '../ui/icons';
import {
  REPORT_FORMATS,
  reportTypesForScope,
  reportTypeLabel,
  validateReportDateRange,
} from '../../lib/validators/reportSchema';
import { useToast } from '../ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { formatRelativeTime } from '../../lib/relativeTime';

let historyIdCounter = 0;

// UI-only copy (icon + one-line description per report type) — purely
// presentational, doesn't touch what the backend actually includes in the
// file. Falls back to a generic doc icon for any type not listed here.
const TYPE_META = {
  investments: { icon: TrendingUpIcon, description: 'Performance and funding status across every investment.' },
  transactions: { icon: WalletIcon, description: 'Payments received and payouts made.' },
  support_tickets: { icon: MessageCircleIcon, description: 'Ticket volume, categories, and resolution activity.' },
  kyc: { icon: ShieldCheckIcon, description: 'Submitted documents and verification status.' },
};

const FORMAT_BADGE_TONE = { csv: 'info', xlsx: 'success' };

function formatDateRange(dateFrom, dateTo) {
  const fmt = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  if (!dateFrom && !dateTo) return 'All time';
  if (dateFrom && dateTo) return `${fmt(dateFrom)} – ${fmt(dateTo)}`;
  return dateFrom ? `From ${fmt(dateFrom)}` : `Through ${fmt(dateTo)}`;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

// Client-side convenience only — every preset just fills the same
// date_from/date_to params the custom-range inputs always sent. The
// request shape to the backend never changes.
function computePreset(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'today':
      return { from: isoDate(today), to: isoDate(today) };
    case '7d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: isoDate(from), to: isoDate(today) };
    }
    case '30d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: isoDate(from), to: isoDate(today) };
    }
    case 'this_month':
      return { from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDate(today) };
    case 'last_month': {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: isoDate(from), to: isoDate(to) };
    }
    case 'this_year':
      return { from: isoDate(new Date(today.getFullYear(), 0, 1)), to: isoDate(today) };
    default:
      return { from: '', to: '' };
  }
}

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_year', label: 'This year' },
  { key: 'all', label: 'All time' },
];

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, onOutside]);
}

/** Custom popover select for report type — icon + description per option,
 * instead of a bare native <select>. */
function TypeDropdown({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const selected = options.find((o) => o.value === value) || options[0];
  const SelectedIcon = TYPE_META[selected?.value]?.icon || FileTextIcon;

  return (
    <div className="relative" ref={ref}>
      <label className="block text-sm font-medium text-foreground-muted mb-1.5">Report type</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground hover:border-border-strong transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      >
        <span className="h-8 w-8 rounded-lg bg-accent-soft text-accent flex items-center justify-center shrink-0">
          <SelectedIcon className="h-4 w-4" />
        </span>
        <span className="flex-1 text-left truncate">{selected?.label}</span>
        <ChevronDownIcon className={clsx('h-4 w-4 text-foreground-subtle transition-transform duration-150', open && 'rotate-180')} />
      </button>

      <div
        className={clsx(
          'absolute z-20 mt-2 w-full origin-top rounded-xl border border-border bg-background-surface shadow-card-hover overflow-hidden transition-all duration-150',
          open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {options.map((o) => {
          const meta = TYPE_META[o.value] || {};
          const Icon = meta.icon || FileTextIcon;
          const isSelected = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={clsx(
                'w-full flex items-start gap-3 px-3.5 py-3 text-left hover:bg-background-elevated transition-colors',
                isSelected && 'bg-accent-soft/40'
              )}
            >
              <span
                className={clsx(
                  'h-8 w-8 rounded-lg flex items-center justify-center shrink-0',
                  isSelected ? 'bg-accent text-white' : 'bg-background-elevated text-foreground-muted'
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">{o.label}</span>
                {meta.description && <span className="block text-xs text-foreground-subtle mt-0.5">{meta.description}</span>}
              </span>
              {isSelected && <CheckIcon className="h-4 w-4 text-accent shrink-0 mt-1" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Segmented pill toggle for format — only ever CSV/Excel, so a two-way
 * toggle reads better than a dropdown for a binary choice. */
function FormatToggle({ value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-muted mb-1.5">Format</label>
      <div className="inline-flex w-full rounded-xl bg-background-elevated border border-border p-1 gap-1">
        {REPORT_FORMATS.map((f) => {
          const active = f.value === value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => onChange(f.value)}
              className={clsx(
                'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
                active ? 'bg-accent text-white shadow-glow-sm' : 'text-foreground-muted hover:text-foreground'
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-foreground-subtle">CSV or Excel only · Excel capped at 10,000 rows</p>
    </div>
  );
}

/** Custom date-range popover — quick presets plus a custom From/To pair,
 * replacing the old bare pair of native date inputs. */
function DateRangeDropdown({ dateFrom, dateTo, onApply }) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  useEffect(() => {
    if (open) {
      setDraftFrom(dateFrom);
      setDraftTo(dateTo);
    }
  }, [open, dateFrom, dateTo]);

  const applyPreset = (key) => {
    const { from, to } = computePreset(key);
    onApply(from, to);
    setOpen(false);
  };

  const applyCustom = () => {
    onApply(draftFrom, draftTo);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <label className="block text-sm font-medium text-foreground-muted mb-1.5">Date range</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground hover:border-border-strong transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      >
        <span className="h-8 w-8 rounded-lg bg-accent-soft text-accent flex items-center justify-center shrink-0">
          <CalendarIcon className="h-4 w-4" />
        </span>
        <span className="flex-1 text-left truncate">{formatDateRange(dateFrom, dateTo)}</span>
        <ChevronDownIcon className={clsx('h-4 w-4 text-foreground-subtle transition-transform duration-150', open && 'rotate-180')} />
      </button>

      <div
        className={clsx(
          'absolute z-20 mt-2 w-full sm:w-[420px] right-0 origin-top-right rounded-xl border border-border bg-background-surface shadow-card-hover overflow-hidden transition-all duration-150',
          open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        <div className="flex flex-wrap gap-1.5 p-3 border-b border-border">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground-muted hover:border-accent/40 hover:text-accent hover:bg-accent-soft/40 transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="p-3 space-y-3">
          <p className="text-xs font-medium text-foreground-subtle">Custom range</p>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              className="w-full rounded-lg bg-background-elevated border border-border px-2.5 py-2 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
            <input
              type="date"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              className="w-full rounded-lg bg-background-elevated border border-border px-2.5 py-2 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setDraftFrom('');
                setDraftTo('');
              }}
              className="text-xs text-foreground-subtle hover:text-foreground transition-colors"
            >
              Clear
            </button>
            <Button type="button" size="sm" onClick={applyCustom}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared Reports UI for both admin (GET /admin/reports, view-reports
 * permission) and investor (GET /investor/reports, restricted type list,
 * scoped automatically) — same layout and behavior either side, just a
 * different type list and mutation hook. Same request contract as before
 * (type/format/date_from/date_to) — the custom dropdowns below only
 * change how those four values get set, never what gets sent.
 *
 * There's no `GET .../reports/history` endpoint — nothing is persisted
 * server-side per the docs — so "Recently generated" is plain component
 * state. Re-download re-issues the same request with the same params
 * (simpler than holding blobs in memory for the session, and just as
 * correct since every request is idempotent).
 */
export default function ReportsScreen({ scope, useGenerateReportMutation, permissionDeniedMessage }) {
  const typeOptions = reportTypesForScope(scope);
  const toast = useToast();
  const [generateReport, { isLoading }] = useGenerateReportMutation();

  const [type, setType] = useState(typeOptions[0]?.value || '');
  const [format, setFormat] = useState('csv');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formError, setFormError] = useState(null);
  const [history, setHistory] = useState([]);
  const [redownloadingId, setRedownloadingId] = useState(null);

  const runReport = async (params) => {
    try {
      await generateReport(params).unwrap();
      return true;
    } catch (err) {
      if (err?.status === 422 && params.format === 'xlsx') {
        throw new Error(err?.data?.message || 'This report is too large for Excel — try CSV instead.');
      }
      throw new Error(authErrorMessage(err, { forbidden: permissionDeniedMessage, fallback: 'Could not generate this report.' }));
    }
  };

  const onGenerate = async (e) => {
    e.preventDefault();
    setFormError(null);

    const dateError = validateReportDateRange(dateFrom, dateTo);
    if (dateError) {
      setFormError(dateError);
      return;
    }

    const params = { type, format, date_from: dateFrom || undefined, date_to: dateTo || undefined };
    try {
      await runReport(params);
      toast.push({ tone: 'success', message: 'Report downloaded.' });
      setHistory((h) => [
        { id: ++historyIdCounter, type, format, dateFrom, dateTo, generatedAt: new Date().toISOString() },
        ...h,
      ]);
    } catch (err) {
      setFormError(err.message);
    }
  };

  const onRedownload = async (entry) => {
    setRedownloadingId(entry.id);
    try {
      await runReport({ type: entry.type, format: entry.format, date_from: entry.dateFrom || undefined, date_to: entry.dateTo || undefined });
      toast.push({ tone: 'success', message: 'Report downloaded.' });
    } catch (err) {
      toast.push({ tone: 'danger', message: err.message });
    } finally {
      setRedownloadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-2xl accent-gradient text-white flex items-center justify-center shrink-0 shadow-glow-sm">
          <FileTextIcon className="h-5 w-5" />
        </span>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-foreground">Reports</h1>
            <Badge tone="success">Live</Badge>
          </div>
          <p className="text-foreground-muted text-sm mt-0.5">Generate and download a report.</p>
        </div>
      </div>

      <Card className="relative z-10">
        <form onSubmit={onGenerate} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TypeDropdown options={typeOptions} value={type} onChange={setType} />
            <FormatToggle value={format} onChange={setFormat} />
            <DateRangeDropdown
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
              }}
            />
          </div>

          {formError && <p className="text-sm text-danger">{formError}</p>}

          <Button type="submit" isLoading={isLoading && redownloadingId === null} className="w-full sm:w-auto">
            <DownloadIcon className="h-4 w-4" />
            Generate report
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader title="Recently generated" />
        {history.length === 0 ? (
          <EmptyState
            icon={FileTextIcon}
            title="Nothing generated yet"
            body="Reports you generate this session show up here for a quick re-download."
          />
        ) : (
          <ul className="divide-y divide-border">
            {history.map((entry) => {
              const Icon = TYPE_META[entry.type]?.icon || FileTextIcon;
              return (
                <li key={entry.id} className="animate-fade-in-up flex items-center justify-between gap-3 py-3.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-9 w-9 rounded-lg bg-accent-soft text-accent flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">{reportTypeLabel(entry.type)}</p>
                        <Badge tone={FORMAT_BADGE_TONE[entry.format] || 'neutral'} className="!px-1.5 !py-0.5 text-[10px] shrink-0">
                          {entry.format.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-xs text-foreground-subtle mt-0.5 truncate">
                        {formatDateRange(entry.dateFrom, entry.dateTo)} · {formatRelativeTime(entry.generatedAt)}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRedownload(entry)}
                    isLoading={redownloadingId === entry.id}
                    className="shrink-0"
                  >
                    <DownloadIcon className="h-3.5 w-3.5" />
                    Download
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-4 pt-4 border-t border-border text-xs text-foreground-subtle">
          Each download streams directly — nothing is stored after the request.
        </p>
      </Card>
    </div>
  );
}
